/**
 * Image URL resolution for API payloads.
 *
 * The API returns an image as a bare string, an object (`{ url }` / `{ imageUrl }`), or a
 * path relative to the API host, so every list that renders one needs the same three
 * cases handled. Two pages had grown their own private copy of this; the walk-in modal
 * needs a third, so it lives here instead.
 *
 * (The local copies in `pages/freelancer/FreelancerProducts.js` and
 * `pages/customer/BookingConfirmation.js` are equivalent and could be pointed at this —
 * left alone for now rather than churning working pages.)
 */

/**
 * Pull a usable, absolute image URL out of a product-ish object.
 *
 * @param {object} product - anything with `images` (string | object | array), `imageUrl`
 *                           or `image`.
 * @param {string} apiBaseUrl - API origin, used to absolutise a stored relative path.
 * @returns {string} absolute URL, a data URL, or "" when there is nothing to show.
 */
export function resolveProductImageUrl(product, apiBaseUrl) {
  const candidate =
    (Array.isArray(product?.images) && product.images.length > 0
      ? product.images[0]
      : null) ||
    product?.imageUrl ||
    product?.image ||
    null;

  const raw =
    typeof candidate === "string"
      ? candidate
      : candidate?.url || candidate?.imageUrl || "";

  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;

  const cleanBase = String(apiBaseUrl || "").replace(/\/+$/, "");
  const normalizedPath = raw.startsWith("/") ? raw : `/${raw}`;
  return cleanBase ? `${cleanBase}${normalizedPath}` : raw;
}

export default resolveProductImageUrl;
